// Headless, app-wide session keep-alive — the sliding-session driver (F2/#808).
//
// The session is a 24 h HttpOnly cookie. Originally it expired 24 h after login
// regardless of activity, so a long field shift could cross the boundary
// mid-work and the next capture upload 401'd. This component pushes the expiry
// forward by sliding the cookie (POST /auth/refresh via `refreshSession`) on
// AUTHENTICATED ACTIVITY, so an active surveyor never crosses it.
//
// When it slides (each debounced by MIN_SLIDE_INTERVAL_MS so a flapping radio /
// rapid tab switching can't hammer the endpoint):
//   - once when the session becomes authenticated;
//   - when the device comes back online;
//   - when the tab becomes visible again (foregrounded);
//   - on a periodic timer (SLIDE_INTERVAL_MS) so a continuously-foregrounded
//     session that never fires the events above still slides.
//
// On a successful slide it ALSO propagates the slid `sessionExpiresAt` into the
// offline auth-cache (audit-1 #3): the cache's offline-fallback boundary is
// written only by `GET /me`, so without this a slid session would evict its warm
// `/me` on the STALE pre-slide boundary at a cold offline launch — undoing the
// slide for the exact offline-survival path it should protect. `refreshSession`
// additionally lifts any upload pause on a 2xx (the convergent resume point), so
// a session that recovers via a slide also un-pauses uploads.
//
// Mounted app-wide (a sibling of the router, mirrors AutoRescueHeldCaptures) so
// it runs for any authenticated role and survives route changes. Renders
// nothing. Best-effort throughout: a failed slide leaves the cookie's current
// expiry intact and the next trigger retries (never throws, never surfaces UI).
// The slide is additive safety — the upload-path re-auth recovery
// (connector-upload-auth.ts) is what actually prevents the queue jam if the
// session does expire.

import { useEffect } from 'react'

import { refreshSession } from '@/hooks/session-refresh'
import type { MeResponse } from '@/hooks/use-me'

import { loadCachedMe, saveCachedMe } from './auth-cache'
import { useAuth } from './auth-provider'

// Debounce window: ignore re-triggers inside this window so flapping
// visibility/online events can't spam /auth/refresh. Applies even to a failed
// slide (it is a rate guard on the endpoint, not a success gate).
const MIN_SLIDE_INTERVAL_MS = 5 * 60 * 1000 // 5 min
// Periodic slide so a session foregrounded continuously (no visibility/online
// events) still slides well inside the 24 h window.
const SLIDE_INTERVAL_MS = 30 * 60 * 1000 // 30 min

// Module-level so the debounce survives React re-mounts (incl. StrictMode's dev
// double-mount), which a component-local ref would not — preventing a duplicate
// slide per load. Mirrors AutoRescueHeldCaptures.
let lastSlideAt = Number.NEGATIVE_INFINITY

/** Test seam — reset the module-level debounce between cases. */
export function __resetSessionKeepAliveForTest(): void {
  lastSlideAt = Number.NEGATIVE_INFINITY
}

/**
 * Slide the session and, on success, propagate the slid expiry into the offline
 * cache. Best-effort — never throws. `refreshSession` resumes the upload pause
 * latch on a 2xx, so this also un-pauses uploads when the session recovers.
 */
async function slideAndCache(user: MeResponse | undefined): Promise<void> {
  try {
    const result = await refreshSession()
    if (result.ok && result.sessionExpiresAt !== undefined) {
      // Merge the slid boundary onto the FRESHEST known /me, not the (possibly
      // stale) closure snapshot — a concurrent live /me may have written newer
      // identity fields while this slide was in flight (audit-2 #3 LWW). The
      // slide is then a pure boundary-bump: it never regresses identity fields.
      const base = loadCachedMe() ?? user
      if (base !== undefined) {
        saveCachedMe({ ...base, sessionExpiresAt: result.sessionExpiresAt })
      }
    }
  } catch {
    // Best-effort: a failed slide leaves the cookie's current expiry intact and
    // the next trigger (interval / visibility / online) retries.
  }
}

export interface SessionKeepAliveProps {
  /** Test seam — inject a fake slide. Defaults to the real slide-and-cache. */
  slide?: (user: MeResponse | undefined) => Promise<void>
}

export function SessionKeepAlive({
  slide = slideAndCache,
}: SessionKeepAliveProps = {}): null {
  const { status, user } = useAuth()

  useEffect(() => {
    if (status !== 'authenticated') return

    function maybeSlide(): void {
      if (Date.now() - lastSlideAt < MIN_SLIDE_INTERVAL_MS) return
      // Stamp BEFORE the async slide so the debounce rate-limits the endpoint
      // even when a slide fails; sliding is best-effort and the next trigger
      // (interval / visibility / online) retries, so a missed retry is harmless.
      lastSlideAt = Date.now()
      void slide(user)
    }

    function onVisible(): void {
      if (document.visibilityState === 'visible') maybeSlide()
    }

    // Slide on entry — pushes the window forward as soon as the app is up.
    maybeSlide()

    const interval = setInterval(maybeSlide, SLIDE_INTERVAL_MS)
    window.addEventListener('online', maybeSlide)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(interval)
      window.removeEventListener('online', maybeSlide)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [status, slide, user])

  return null
}
